function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

const ENABLE_MODERN_PRODUCT_VIDEO_PLAYER = true;
const MODERN_PRODUCT_VIDEO_HOST_SELECTORS = [
  '.deferred-media--video',
  '.modern-video-host',
  '.classic-slideshow__video-hosted',
  '.image-with-text-slideshow__video-hosted',
  '.preview-column__video-wrapper',
  '.ecom-element_video-iframe',
  '.ecom-product-single__media--video',
];
const MODERN_PRODUCT_VIDEO_SELECTOR = [
  'video[controls]',
  'video[data-enable-modern-video-player]',
  '.ecom-element_video-iframe video',
  '.ecom-product-single__media--video video',
].join(', ');
const MODERN_PRODUCT_VIDEO_HOST_SELECTOR = MODERN_PRODUCT_VIDEO_HOST_SELECTORS.join(', ');

const clearProductVideoBoundState = (video) => {
  if (!video) return;
  delete video.dataset.productVideoBound;
  video.removeAttribute('data-product-video-bound');
};

const hasSlideshowHostedCustomCover = (container) =>
  Boolean(
    container &&
      (
        container.classList.contains('classic-slideshow__video-hosted--has-cover') ||
        container.classList.contains('image-with-text-slideshow__video-hosted--has-cover')
      )
  );

const syncSlideshowHostedPosterState = (container, video, restorePoster = false) => {
  if (!container || !video) return;

  const currentPoster = video.getAttribute('poster');
  if (currentPoster && !video.dataset.videoOriginalPoster) {
    video.dataset.videoOriginalPoster = currentPoster;
  }

  const originalPoster = video.dataset.videoOriginalPoster;
  if (!hasSlideshowHostedCustomCover(container)) {
    if (restorePoster && originalPoster && !video.getAttribute('poster')) {
      video.setAttribute('poster', originalPoster);
    }
    return;
  }

  if (restorePoster) {
    if (originalPoster && !video.getAttribute('poster')) {
      video.setAttribute('poster', originalPoster);
    }
    return;
  }

  if (video.hasAttribute('poster')) {
    video.removeAttribute('poster');
  }
};

const createProductVideoCoverPlayButton = (label = 'Play video') => {
  const resolvedLabel = label || 'Play video';
  const button = document.createElement('button');
  button.className = 'product-video-player__cover-play';
  button.setAttribute('type', 'button');
  button.setAttribute('data-video-cover-play', '');
  button.setAttribute('data-product-video-cover-play-generated', 'true');
  button.setAttribute('aria-label', resolvedLabel);

  const icon = document.createElement('span');
  icon.className = 'product-video-player__cover-play-icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.innerHTML = `
    <svg viewBox="0 0 24 24" focusable="false">
      <path d="M7.85 4.75L17.85 10.18C19.15 10.89 19.15 13.11 17.85 13.82L7.85 19.25C6.48 19.99 5.1 19.05 5.1 17.49V6.51C5.1 4.95 6.48 4.01 7.85 4.75Z"></path>
    </svg>
  `;
  button.appendChild(icon);
  return button;
};

const createProductVideoControls = () => {
  const controls = document.createElement('div');
  controls.className = 'product-video-player__controls';
  controls.setAttribute('data-product-video-controls', '');
  controls.setAttribute('data-product-video-controls-generated', 'true');
  controls.setAttribute('role', 'group');
  controls.setAttribute('aria-label', 'Video controls');
  controls.innerHTML = `
    <div class="product-video-player__timeline-row">
      <input class="product-video-player__timeline" type="range" min="0" max="1000" value="0" step="1" data-video-progress aria-label="Video timeline">
    </div>
    <div class="product-video-player__actions-row">
      <div class="product-video-player__cluster product-video-player__cluster--left">
        <button class="product-video-player__button product-video-player__button--play" type="button" data-video-toggle-play aria-label="Play video">
          <span class="product-video-player__icon product-video-player__icon--play">
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
              <path d="M7.85 4.75L17.85 10.18C19.15 10.89 19.15 13.11 17.85 13.82L7.85 19.25C6.48 19.99 5.1 19.05 5.1 17.49V6.51C5.1 4.95 6.48 4.01 7.85 4.75Z" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"></path>
            </svg>
          </span>
          <span class="product-video-player__icon product-video-player__icon--pause">
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
              <path d="M8 6.25v11.5M16 6.25v11.5" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"></path>
            </svg>
          </span>
        </button>
        <div class="product-video-player__volume-group">
          <button class="product-video-player__button" type="button" data-video-toggle-mute aria-label="Mute video">
            <span class="product-video-player__icon product-video-player__icon--volume">
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
                <path d="M5.5 9.2h3.2L13 5.8v12.4l-4.3-3.4H5.5V9.2Z" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"></path>
                <path d="M15 9.2a4 4 0 0 1 0 5.6" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"></path>
                <path d="M17.6 6.7a7.55 7.55 0 0 1 0 10.6" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"></path>
              </svg>
            </span>
            <span class="product-video-player__icon product-video-player__icon--muted">
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
                <path d="M5.5 9.2h3.2L13 5.8v12.4l-4.3-3.4H5.5V9.2Z" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"></path>
                <path d="M7.2 7 17.2 17" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"></path>
              </svg>
            </span>
          </button>
          <input class="product-video-player__volume" type="range" min="0" max="100" value="100" step="1" data-video-volume aria-label="Video volume">
        </div>
        <span class="product-video-player__time product-video-player__time--elapsed" data-video-current-time>0:00</span>
      </div>
      <div class="product-video-player__cluster product-video-player__cluster--right">
        <button class="product-video-player__button" type="button" data-video-fullscreen aria-label="Toggle full screen">
          <span class="product-video-player__icon">
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
              <path d="M6 9V6h3M15 6h3v3M18 15v3h-3M9 18H6v-3" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
          </span>
        </button>
      </div>
    </div>
  `;
  return controls;
};

const formatVideoTime = (totalSeconds) => {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return '0:00';
  const safeSeconds = Math.floor(totalSeconds);
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;
  if (hours > 0) return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const setVideoRangeProgress = (input, value) => {
  if (!input) return;
  const boundedValue = Math.max(0, Math.min(100, value));
  input.style.setProperty('--range-progress', `${boundedValue}%`);
};

const hydrateDeferredVideoSources = (video) => {
  if (!video || video.dataset.deferVideoUntilPlay !== 'true') return;
  if (video.dataset.deferredVideoSourceLoaded === 'true') return;

  let sourceWasHydrated = false;
  const deferredVideoSrc = video.dataset.src;
  if (deferredVideoSrc && !video.getAttribute('src')) {
    video.setAttribute('src', deferredVideoSrc);
    sourceWasHydrated = true;
  }

  video.querySelectorAll('source[data-src]').forEach((source) => {
    if (!source.getAttribute('src')) {
      source.setAttribute('src', source.dataset.src);
      sourceWasHydrated = true;
    }
  });

  video.dataset.deferredVideoSourceLoaded = 'true';
  if (sourceWasHydrated) video.load();
};

const shouldEnhanceModernVideo = (video) => {
  if (!video) return false;
  if (video.closest('template')) return false;
  if (video.classList.contains('ecom-video-background')) return false;
  if (video.hasAttribute('data-disable-modern-video-player')) return false;

  const hostContainer = video.closest(MODERN_PRODUCT_VIDEO_HOST_SELECTOR) || video.parentElement;
  if (!hostContainer || hostContainer.hasAttribute('data-disable-modern-video-player')) return false;

  const ecomVideoHost = video.closest('.ecom-element_video-iframe');
  const hasEcomPosterUi = Boolean(ecomVideoHost && ecomVideoHost.querySelector('.ecom-video-poster, .ecom-video_overlay-icon'));
  const hasControls = video.hasAttribute('controls') || video.controls || video.dataset.enableModernVideoPlayer === 'true';
  const isEcomProductVideo = Boolean(video.closest('.ecom-product-single__media--video'));

  return hasControls || hasEcomPosterUi || isEcomProductVideo;
};

const bindModernVideoPlayer = (container, video) => {
  if (!container || !video) return;

  const existingController = container._productVideoController;
  if (existingController) {
    if (existingController.video === video) {
      existingController.refresh();
      return;
    }

    existingController.destroy();
  }

  if (video.dataset.productVideoBound === 'true') {
    clearProductVideoBoundState(video);
  }

  if (!ENABLE_MODERN_PRODUCT_VIDEO_PLAYER) {
    syncSlideshowHostedPosterState(container, video, true);
    video.dataset.productVideoBound = 'true';
    video.controls = true;
    video.setAttribute('controls', 'controls');
    container.classList.add('product-video-player--native-fallback');
    container.classList.remove(
      'product-video-player--enhanced',
      'modern-video-host--positioned',
      'video-is-playing',
      'video-is-muted',
      'video-has-started'
    );
    const existingCustomControls = container.querySelector('[data-product-video-controls]');
    if (existingCustomControls) existingCustomControls.remove();
    const existingCoverPlayButton = container.querySelector('[data-video-cover-play]');
    if (
      existingCoverPlayButton &&
      existingCoverPlayButton.getAttribute('data-product-video-cover-play-generated') === 'true'
    ) {
      existingCoverPlayButton.remove();
    }
    return;
  }

  let showCoverPlayIcon = container.dataset.showCoverPlayIcon === 'true' || video.dataset.showCoverPlayIcon === 'true';
  let coverPlayLabel = container.dataset.videoCoverPlayLabel || video.dataset.videoCoverPlayLabel || 'Play video';
  let controls = container.querySelector('[data-product-video-controls]');
  const controlsWereInjected = !controls;
  let coverPlayButton = container.querySelector('[data-video-cover-play]');
  const coverPlayButtonWasInjected = showCoverPlayIcon && !coverPlayButton;
  const cleanupFns = [];
  const addManagedListener = (target, eventName, handler, options) => {
    if (!target) return;
    target.addEventListener(eventName, handler, options);
    cleanupFns.push(() => target.removeEventListener(eventName, handler, options));
  };
  const stopControlPropagation = (event) => event.stopPropagation();
  let progressAnimationFrame = null;
  let controller = null;

  try {
    container.classList.remove('product-video-player--native-fallback');
    syncSlideshowHostedPosterState(container, video, false);

    if (
      !showCoverPlayIcon &&
      coverPlayButton &&
      coverPlayButton.getAttribute('data-product-video-cover-play-generated') === 'true'
    ) {
      coverPlayButton.remove();
      coverPlayButton = null;
    }

    if (showCoverPlayIcon && !coverPlayButton) {
      coverPlayButton = createProductVideoCoverPlayButton(coverPlayLabel);
      container.appendChild(coverPlayButton);
    }

    if (!controls) {
      controls = createProductVideoControls();
      container.appendChild(controls);
    }

    const playButton = controls.querySelector('[data-video-toggle-play]');
    const muteButton = controls.querySelector('[data-video-toggle-mute]');
    const progressInput = controls.querySelector('[data-video-progress]');
    const volumeInput = controls.querySelector('[data-video-volume]');
    const fullscreenButton = controls.querySelector('[data-video-fullscreen]');
    const currentTimeText = controls.querySelector('[data-video-current-time]');

    if (
      !playButton ||
      !muteButton ||
      !progressInput ||
      !volumeInput ||
      !fullscreenButton ||
      !currentTimeText
    ) {
      throw new Error('Modern controls markup is incomplete.');
    }

    video.dataset.productVideoBound = 'true';
    video.setAttribute('playsinline', '');
    const deferVideoUntilPlay = video.dataset.deferVideoUntilPlay === 'true';
    if (deferVideoUntilPlay) {
      video.setAttribute('preload', 'none');
    } else if (!video.getAttribute('preload') || video.getAttribute('preload') === 'none') {
      video.setAttribute('preload', 'metadata');
    }

    if (!container.classList.contains('modern-video-host')) {
      container.classList.add('modern-video-host');
      container.dataset.productVideoAddedHostClass = 'true';
    }
    if (window.getComputedStyle(container).position === 'static') {
      container.classList.add('modern-video-host--positioned');
      container.dataset.productVideoAddedPositionClass = 'true';
    }

    const enableCustomUi = () => {
      if (!container.classList.contains('product-video-player--enhanced')) {
        container.classList.add('product-video-player--enhanced');
      }
      syncSlideshowHostedPosterState(container, video, false);
      if (video.controls || video.hasAttribute('controls')) video.removeAttribute('controls');
    };

    const setSafeVolume = (nextVolume) => {
      try {
        video.volume = nextVolume;
      } catch (error) {
        // Some mobile browsers block programmatic volume changes.
      }
    };

    const stopProgressLoop = () => {
      if (progressAnimationFrame === null) return;
      window.cancelAnimationFrame(progressAnimationFrame);
      progressAnimationFrame = null;
    };

    const runProgressLoop = () => {
      progressAnimationFrame = null;
      if (video.paused || video.ended) return;
      updateProgress();
      updateTimestamps();
      updateStartedState();
      progressAnimationFrame = window.requestAnimationFrame(runProgressLoop);
    };

    const startProgressLoop = () => {
      if (progressAnimationFrame !== null) return;
      progressAnimationFrame = window.requestAnimationFrame(runProgressLoop);
    };

    const updateProgress = () => {
      const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 0;
      const progress = duration > 0 ? (video.currentTime / duration) * 100 : 0;
      progressInput.value = String(Math.round((progress / 100) * 1000));
      setVideoRangeProgress(progressInput, progress);
    };

    const updateTimestamps = () => {
      const nextTime = formatVideoTime(video.currentTime);
      if (currentTimeText.textContent !== nextTime) currentTimeText.textContent = nextTime;
    };

    const updateStartedState = () => {
      syncSlideshowHostedPosterState(container, video, container.classList.contains('product-video-player--native-fallback'));
      container.classList.toggle('video-has-started', video.currentTime > 0.05 || !video.paused || video.ended);
    };

    const updatePlayState = () => {
      const isPlaying = !video.paused && !video.ended;
      container.classList.toggle('video-is-playing', isPlaying);
      playButton.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
    };

    const updateMuteState = () => {
      const currentVolume = Number.isFinite(video.volume) ? video.volume : 1;
      const isMuted = video.muted || currentVolume === 0;
      container.classList.toggle('video-is-muted', isMuted);
      muteButton.setAttribute('aria-label', isMuted ? 'Unmute video' : 'Mute video');
      volumeInput.value = String(Math.round(currentVolume * 100));
      setVideoRangeProgress(volumeInput, currentVolume * 100);
    };

    const syncUi = () => {
      updateProgress();
      updateTimestamps();
      updatePlayState();
      updateStartedState();
      updateMuteState();
    };

    const requestPlay = () => {
      window.pauseAllMedia();
      hydrateDeferredVideoSources(video);
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
    };

    const togglePlayback = () => {
      if (video.paused || video.ended) {
        requestPlay();
        return;
      }
      video.pause();
    };

    const destroy = () => {
      stopProgressLoop();
      cleanupFns.splice(0).forEach((cleanup) => cleanup());
      if (container._productVideoController === controller) {
        delete container._productVideoController;
      }
      clearProductVideoBoundState(video);
    };

    const requestRebind = () => {
      destroy();
      bindModernVideoPlayer(container, video);
    };

    const refresh = () => {
      const nextShowCoverPlayIcon =
        container.dataset.showCoverPlayIcon === 'true' || video.dataset.showCoverPlayIcon === 'true';
      const nextCoverPlayLabel = container.dataset.videoCoverPlayLabel || video.dataset.videoCoverPlayLabel || 'Play video';
      const existingControls = container.querySelector('[data-product-video-controls]');
      const existingCoverPlay = container.querySelector('[data-video-cover-play]');

      if (!existingControls || (nextShowCoverPlayIcon && !existingCoverPlay)) {
        requestRebind();
        return;
      }

      if (
        !nextShowCoverPlayIcon &&
        existingCoverPlay &&
        existingCoverPlay.getAttribute('data-product-video-cover-play-generated') === 'true'
      ) {
        existingCoverPlay.remove();
        coverPlayButton = null;
      } else if (existingCoverPlay) {
        coverPlayButton = existingCoverPlay;
        coverPlayButton.setAttribute('aria-label', nextCoverPlayLabel);
      }

      controls = existingControls;
      showCoverPlayIcon = nextShowCoverPlayIcon;
      coverPlayLabel = nextCoverPlayLabel;
      syncUi();
      enableCustomUi();
    };

    controller = {
      video,
      refresh,
      destroy,
    };
    container._productVideoController = controller;

    ['click', 'pointerdown', 'mousedown', 'touchstart', 'touchmove', 'wheel'].forEach((eventName) => {
      addManagedListener(controls, eventName, stopControlPropagation);
    });

    if (coverPlayButton) {
      ['click', 'pointerdown', 'mousedown', 'touchstart'].forEach((eventName) => {
        addManagedListener(coverPlayButton, eventName, stopControlPropagation);
      });
      addManagedListener(coverPlayButton, 'click', (event) => {
        event.preventDefault();
        requestPlay();
      });
    }

    addManagedListener(playButton, 'click', () => {
      togglePlayback();
    });
    addManagedListener(muteButton, 'click', () => {
      video.muted = !video.muted;
      const currentVolume = Number.isFinite(video.volume) ? video.volume : 0;
      if (!video.muted && currentVolume === 0) setSafeVolume(0.5);
      updateMuteState();
    });

    const onScrub = () => {
      if (!Number.isFinite(video.duration) || video.duration <= 0) return;
      const rawValue = Number.isFinite(progressInput.valueAsNumber)
        ? progressInput.valueAsNumber
        : Number.parseFloat(progressInput.value || '0');
      const nextTime = (rawValue / 1000) * video.duration;
      video.currentTime = Math.max(0, Math.min(video.duration, nextTime));
      updateProgress();
      updateTimestamps();
    };
    addManagedListener(progressInput, 'input', onScrub);
    addManagedListener(progressInput, 'change', onScrub);

    const onVolumeChange = () => {
      const rawValue = Number.isFinite(volumeInput.valueAsNumber)
        ? volumeInput.valueAsNumber
        : Number.parseFloat(volumeInput.value || '100');
      const nextVolume = Math.max(0, Math.min(1, rawValue / 100));
      setSafeVolume(nextVolume);
      video.muted = nextVolume === 0;
      updateMuteState();
    };
    addManagedListener(volumeInput, 'input', onVolumeChange);
    addManagedListener(volumeInput, 'change', onVolumeChange);

    addManagedListener(fullscreenButton, 'click', () => {
      const target = container;
      if (document.fullscreenElement) {
        const exitPromise = document.exitFullscreen();
        if (exitPromise && typeof exitPromise.catch === 'function') exitPromise.catch(() => {});
        return;
      }
      if (target.requestFullscreen) {
        const fullscreenPromise = target.requestFullscreen();
        if (fullscreenPromise && typeof fullscreenPromise.catch === 'function') fullscreenPromise.catch(() => {});
      } else if (video.webkitEnterFullscreen) {
        video.webkitEnterFullscreen();
      }
    });

    const legacyPlayTriggers = container.querySelectorAll(
      '.ecom-video_overlay-icon, .ecom-video-poster, .ecom-product-single__media--play-control'
    );
    legacyPlayTriggers.forEach((trigger) => {
      addManagedListener(trigger, 'click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        togglePlayback();
      });
    });

    addManagedListener(video, 'click', () => {
      togglePlayback();
    });
    addManagedListener(video, 'loadedmetadata', syncUi);
    addManagedListener(video, 'timeupdate', () => {
      updateProgress();
      updateTimestamps();
      updateStartedState();
    });
    addManagedListener(video, 'durationchange', updateTimestamps);
    addManagedListener(video, 'play', () => {
      updatePlayState();
      updateStartedState();
      startProgressLoop();
    });
    addManagedListener(video, 'pause', () => {
      stopProgressLoop();
      updatePlayState();
    });
    addManagedListener(video, 'ended', () => {
      stopProgressLoop();
      updatePlayState();
      updateStartedState();
    });
    addManagedListener(video, 'volumechange', updateMuteState);
    addManagedListener(video, 'seeking', updateStartedState);
    addManagedListener(video, 'seeked', () => {
      updateProgress();
      updateTimestamps();
      updateStartedState();
    });

    syncUi();
    if (!video.paused && !video.ended) startProgressLoop();
    enableCustomUi();
  } catch (error) {
    console.error('Modern video player failed to initialize. Falling back to native controls.', error);
    cleanupFns.splice(0).forEach((cleanup) => cleanup());
    clearProductVideoBoundState(video);
    video.controls = true;
    video.setAttribute('controls', 'controls');
    syncSlideshowHostedPosterState(container, video, true);
    container.classList.add('product-video-player--native-fallback');
    container.classList.remove('product-video-player--enhanced', 'video-is-playing', 'video-is-muted', 'video-has-started');
    if (container._productVideoController?.video === video) {
      delete container._productVideoController;
    }
    if (container.dataset.productVideoAddedHostClass === 'true') {
      container.classList.remove('modern-video-host');
      delete container.dataset.productVideoAddedHostClass;
    }
    if (container.dataset.productVideoAddedPositionClass === 'true') {
      container.classList.remove('modern-video-host--positioned');
      delete container.dataset.productVideoAddedPositionClass;
    }
    if (controlsWereInjected && controls && controls.getAttribute('data-product-video-controls-generated') === 'true') {
      controls.remove();
    }
    if (
      coverPlayButtonWasInjected &&
      coverPlayButton &&
      coverPlayButton.getAttribute('data-product-video-cover-play-generated') === 'true'
    ) {
      coverPlayButton.remove();
    }
  }
};

const initializeProductVideoPlayers = (root = document) => {
  if (!root) return;
  const candidates = new Set();

  if (root.matches && root.matches(MODERN_PRODUCT_VIDEO_SELECTOR)) {
    candidates.add(root);
  }

  if (root.matches && root.matches(MODERN_PRODUCT_VIDEO_HOST_SELECTOR)) {
    root.querySelectorAll('video').forEach((video) => candidates.add(video));
  }

  if (root.querySelectorAll) {
    root.querySelectorAll(MODERN_PRODUCT_VIDEO_SELECTOR).forEach((video) => candidates.add(video));
    root.querySelectorAll(`${MODERN_PRODUCT_VIDEO_HOST_SELECTOR} video`).forEach((video) => candidates.add(video));
  }

  candidates.forEach((video) => {
    const container = video.closest(MODERN_PRODUCT_VIDEO_HOST_SELECTOR) || video.parentElement;
    if (!container) return;

    if (container._productVideoController?.video === video) {
      container._productVideoController.refresh();
      return;
    }

    if (!shouldEnhanceModernVideo(video)) return;
    bindModernVideoPlayer(container, video);
  });
};

const DEFAULT_MUTED_VIDEO_EMBED_SELECTOR = 'iframe[src*="youtube.com/embed"], iframe[src*="player.vimeo.com/video"]';

const applyDefaultMutedToVideo = (video) => {
  if (!video || video.dataset.defaultMutedApplied === 'true') return;
  video.defaultMuted = true;
  video.muted = true;
  video.setAttribute('muted', '');
  video.setAttribute('defaultmuted', '');
  video.dataset.defaultMutedApplied = 'true';
};

const applyDefaultMutedToEmbed = (iframe) => {
  if (!iframe || iframe.dataset.defaultMutedApplied === 'true') return;

  try {
    const url = new URL(iframe.getAttribute('src') || '', window.location.origin);

    if (url.hostname.includes('youtube.com')) {
      url.searchParams.set('mute', '1');
      url.searchParams.set('playsinline', url.searchParams.get('playsinline') || '1');
    } else if (url.hostname.includes('vimeo.com')) {
      url.searchParams.set('muted', '1');
      url.searchParams.set('playsinline', url.searchParams.get('playsinline') || '1');
    } else {
      iframe.dataset.defaultMutedApplied = 'true';
      return;
    }

    const nextSrc = url.toString();
    if (iframe.src !== nextSrc) iframe.src = nextSrc;
  } catch (error) {
    // Ignore malformed embed URLs and leave the original player untouched.
  }

  iframe.dataset.defaultMutedApplied = 'true';
};

const initializeSectionVideoDefaults = (root = document) => {
  if (!root) return;

  if (root.matches && root.matches('video')) {
    applyDefaultMutedToVideo(root);
  }

  if (root.matches && root.matches(DEFAULT_MUTED_VIDEO_EMBED_SELECTOR)) {
    applyDefaultMutedToEmbed(root);
  }

  if (root.querySelectorAll) {
    root.querySelectorAll('video').forEach(applyDefaultMutedToVideo);
    root.querySelectorAll(DEFAULT_MUTED_VIDEO_EMBED_SELECTOR).forEach(applyDefaultMutedToEmbed);
  }
};

let modernVideoObserver = null;
let modernVideoInitScheduled = false;
const requestFrame = window.requestAnimationFrame
  ? window.requestAnimationFrame.bind(window)
  : (callback) => window.setTimeout(callback, 16);

const scheduleModernVideoInitialization = () => {
  if (modernVideoInitScheduled) return;
  modernVideoInitScheduled = true;
  requestFrame(() => {
    modernVideoInitScheduled = false;
    initializeSectionVideoDefaults();
    initializeProductVideoPlayers();
  });
};

const observeModernVideoDom = () => {
  if (modernVideoObserver || !document.body) return;

  modernVideoObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes') {
        const element = mutation.target;
        // DOMTokenList.add() and attribute setters can report a mutation even
        // when the value is unchanged. Refreshing in response to our own
        // identical writes otherwise schedules another full scan every frame.
        if (mutation.oldValue === element.getAttribute(mutation.attributeName)) continue;
        if (
          element?.matches &&
          (
            element.matches('video') ||
            element.matches(MODERN_PRODUCT_VIDEO_SELECTOR) ||
            element.matches(MODERN_PRODUCT_VIDEO_HOST_SELECTOR)
          )
        ) {
          scheduleModernVideoInitialization();
          return;
        }
      }

      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        const element = node;
        if (
          (element.matches &&
            (
              element.matches('video') ||
              element.matches(DEFAULT_MUTED_VIDEO_EMBED_SELECTOR) ||
              element.matches(MODERN_PRODUCT_VIDEO_SELECTOR) ||
              element.matches(MODERN_PRODUCT_VIDEO_HOST_SELECTOR)
            )) ||
          (element.querySelector &&
            (
              element.querySelector('video') ||
              element.querySelector(DEFAULT_MUTED_VIDEO_EMBED_SELECTOR) ||
              element.querySelector(MODERN_PRODUCT_VIDEO_SELECTOR) ||
              element.querySelector(MODERN_PRODUCT_VIDEO_HOST_SELECTOR)
            ))
        ) {
          scheduleModernVideoInitialization();
          return;
        }
      }
    }
  });

  modernVideoObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeOldValue: true,
    attributeFilter: [
      'class',
      'controls',
      'poster',
      'data-enable-modern-video-player',
      'data-hide-video-controls',
      'data-show-cover-play-icon',
      'data-video-controls-mode',
      'data-video-cover-play-label',
    ],
  });
};

initializeSectionVideoDefaults();
initializeProductVideoPlayers();
observeModernVideoDom();
document.addEventListener('DOMContentLoaded', () => {
  initializeSectionVideoDefaults();
  initializeProductVideoPlayers();
  observeModernVideoDom();
});
document.addEventListener('shopify:section:load', (event) => {
  initializeSectionVideoDefaults(event.target);
  initializeProductVideoPlayers(event.target);
});
document.addEventListener('shopify:block:select', (event) => {
  initializeSectionVideoDefaults(event.target);
  initializeProductVideoPlayers(event.target);
});
document.addEventListener('shopify:block:deselect', (event) => {
  initializeSectionVideoDefaults(event.target);
  initializeProductVideoPlayers(event.target);
});

const initializeProductModelInteraction = (modelViewer) => {
  if (!modelViewer) return;
  applySwishModelViewerCamera(modelViewer);
  if (modelViewer.dataset.productModelInteractive === 'true') return;
  modelViewer.dataset.productModelInteractive = 'true';

  // Force interactive controls for all product model-viewer instances.
  modelViewer.setAttribute('camera-controls', '');
  modelViewer.style.touchAction = 'none';

  const stopPropagation = (event) => event.stopPropagation();
  const setGrabbing = () => {
    modelViewer.style.cursor = 'grabbing';
  };
  const setGrab = () => {
    modelViewer.style.cursor = 'grab';
  };

  modelViewer.addEventListener('pointerdown', (event) => {
    stopPropagation(event);
    setGrabbing();
  });

  ['pointermove', 'pointerup', 'pointercancel', 'touchstart', 'touchmove', 'touchend', 'mousedown', 'mousemove', 'wheel'].forEach((eventName) => {
    modelViewer.addEventListener(eventName, stopPropagation);
  });

  ['pointerup', 'pointercancel', 'mouseleave', 'blur'].forEach((eventName) => {
    modelViewer.addEventListener(eventName, setGrab);
  });

  modelViewer.addEventListener('load', () => {
    applySwishModelViewerCamera(modelViewer);
  });
};

const normalizeSwishCameraValue = (rawValue) => {
  if (rawValue == null) return '';
  return String(rawValue).replace(/,/g, ' ').trim().replace(/\s+/g, ' ');
};

const normalizeSwishAngleValue = (rawValue) => {
  const value = normalizeSwishCameraValue(rawValue);
  if (!value || value === 'auto') return value;
  if (/^-?\d+(\.\d+)?$/.test(value)) return `${value}deg`;
  return value;
};

const normalizeSwishCameraOrbit = (rawValue) => {
  const value = normalizeSwishCameraValue(rawValue);
  if (!value) return '';
  const parts = value.split(' ');
  if (parts.length < 2) return '';

  const yaw = normalizeSwishAngleValue(parts[0]);
  const pitch = normalizeSwishAngleValue(parts[1]);
  const distance = parts.length > 2 ? parts.slice(2).join(' ') : 'auto';
  if (!yaw || !pitch) return '';
  return `${yaw} ${pitch} ${distance}`;
};

const normalizeSwishCameraTarget = (rawValue) => {
  const value = normalizeSwishCameraValue(rawValue);
  if (!value) return '';
  const parts = value.split(' ');
  if (parts.length === 1) return `${parts[0]} auto auto`;
  if (parts.length === 2) return `${parts[0]} ${parts[1]} auto`;
  return `${parts[0]} ${parts[1]} ${parts.slice(2).join(' ')}`;
};

const normalizeSwishFieldOfView = (rawValue) => {
  const value = normalizeSwishCameraValue(rawValue);
  if (!value || value === 'auto') return value;
  if (/^-?\d+(\.\d+)?$/.test(value)) return `${value}deg`;
  return value;
};

const applySwishModelViewerCamera = (modelViewer) => {
  if (!modelViewer || !modelViewer.classList || !modelViewer.classList.contains('swish-product-model-viewer')) return;

  const orbit = normalizeSwishCameraOrbit(modelViewer.dataset.swishCameraOrbit || modelViewer.getAttribute('camera-orbit'));
  const target = normalizeSwishCameraTarget(modelViewer.dataset.swishCameraTarget || modelViewer.getAttribute('camera-target'));
  const fieldOfView = normalizeSwishFieldOfView(
    modelViewer.dataset.swishFieldOfView || modelViewer.getAttribute('field-of-view')
  );

  if (orbit) {
    modelViewer.setAttribute('camera-orbit', orbit);
    if ('cameraOrbit' in modelViewer) {
      try {
        modelViewer.cameraOrbit = orbit;
      } catch (error) {
        // Ignore runtime assignment errors and keep attribute fallback.
      }
    }
  }

  if (target) {
    modelViewer.setAttribute('camera-target', target);
    if ('cameraTarget' in modelViewer) {
      try {
        modelViewer.cameraTarget = target;
      } catch (error) {
        // Ignore runtime assignment errors and keep attribute fallback.
      }
    }
  }

  if (fieldOfView) {
    modelViewer.setAttribute('field-of-view', fieldOfView);
  }
};

let swishModelViewerFeatureRequested = false;
let swishModelViewerFeatureRetryScheduled = false;

const requestSwishModelViewerFeature = () => {
  if (swishModelViewerFeatureRequested) return;
  if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
    if (!swishModelViewerFeatureRetryScheduled) {
      swishModelViewerFeatureRetryScheduled = true;
      window.addEventListener(
        'load',
        () => {
          swishModelViewerFeatureRetryScheduled = false;
          requestSwishModelViewerFeature();
        },
        { once: true }
      );
    }
    return;
  }

  swishModelViewerFeatureRequested = true;
  window.Shopify.loadFeatures([
    {
      name: 'model-viewer-ui',
      version: '1.0',
      onLoad: (errors) => {
        if (errors) {
          swishModelViewerFeatureRequested = false;
        }
      },
    },
  ]);
};

const ensureSwishModelViewerFeature = (root = document) => {
  if (!root) return;

  if (root.matches && root.matches('model-viewer.swish-product-model-viewer')) {
    requestSwishModelViewerFeature();
    return;
  }

  if (root.querySelector && root.querySelector('model-viewer.swish-product-model-viewer')) {
    requestSwishModelViewerFeature();
  }
};

const observeProductModelContainer = (container) => {
  if (!container || container.dataset.productModelObserverAttached === 'true') return;
  container.dataset.productModelObserverAttached = 'true';

  const observer = new MutationObserver(() => initializeProductModels(container));
  observer.observe(container, { childList: true, subtree: true });
};

const initializeProductModels = (root = document) => {
  if (!root) return;
  ensureSwishModelViewerFeature(root);

  if (root.matches && root.matches('model-viewer')) {
    initializeProductModelInteraction(root);
  }

  if (root.querySelectorAll) {
    root.querySelectorAll('model-viewer').forEach((modelViewer) => {
      initializeProductModelInteraction(modelViewer);
    });

    root.querySelectorAll('product-model, deferred-media.deferred-media--model, .product-media-container.media-type-model').forEach((container) => {
      observeProductModelContainer(container);
    });
  }
};

initializeProductModels();
document.addEventListener('DOMContentLoaded', () => initializeProductModels());
document.addEventListener('shopify:section:load', (event) => initializeProductModels(event.target));

const ensureEcomPopupDataElement = () => {
  if (!document.body) return;
  const existing = document.getElementById('ecom-popup-data');
  if (existing) {
    if (!document.body.contains(existing)) document.body.appendChild(existing);
    return;
  }
  const popupData = document.createElement('script');
  popupData.id = 'ecom-popup-data';
  popupData.type = 'application/json';
  popupData.textContent = '[]';
  document.body.appendChild(popupData);
};

ensureEcomPopupDataElement();
document.addEventListener('DOMContentLoaded', ensureEcomPopupDataElement);
window.addEventListener('load', ensureEcomPopupDataElement);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  pauseSlideVideos(slide) {
    slide.querySelectorAll('video').forEach((video) => {
      video.pause();
    });
  }

  playSlideVideos(slide) {
    slide.querySelectorAll('video').forEach((video) => {
      if (!video.autoplay) return;

      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    });
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
        this.playSlideVideos(item);
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
        this.pauseSlideVideos(item);
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

if (typeof window !== 'undefined') {
  window.SwishShopifyCaptcha = window.SwishShopifyCaptcha || {};
  const SWISH_CONTACT_BODY_NAME = 'contact[body]';
  const SWISH_CONTACT_BODY_HIDDEN_FIELDS = [
    'contact[Context]',
    'contact[Page URL]',
    'contact[Share URL]',
    'contact[Preview URL]'
  ];

  const swishEscapeSelectorValue = (value) => {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
      return CSS.escape(value);
    }

    return String(value).replace(/["\\]/g, '\\$&');
  };

  const swishIsContactForm = (form) => {
    if (!(form instanceof HTMLFormElement)) return false;
    if (form.querySelector('input[name="form_type"][value="contact"]')) return true;

    const action = (form.getAttribute('action') || '').trim();
    if (!action) return false;

    return /\/contact(?:[#?].*)?$/.test(action) || action.indexOf('/contact') !== -1;
  };

  const swishCollectContactForms = (root = document) => {
    if (!root) return [];

    const forms = [];
    if (root instanceof HTMLFormElement) {
      forms.push(root);
    } else if (root.matches && root.matches('form')) {
      forms.push(root);
    }

    if (typeof root.querySelectorAll === 'function') {
      forms.push(...Array.from(root.querySelectorAll('form')));
    }

    return forms.filter((form, index, list) => {
      return list.indexOf(form) === index && swishIsContactForm(form);
    });
  };

  const swishGetContactFieldLabel = (form, field) => {
    if (!(field instanceof HTMLElement)) return '';

    if (field.dataset && field.dataset.swishContactBodyLabel) {
      return field.dataset.swishContactBodyLabel;
    }

    if (field.id) {
      const label = form.querySelector(`label[for="${swishEscapeSelectorValue(field.id)}"]`);
      if (label && label.textContent) {
        return label.textContent.replace(/\s+/g, ' ').replace(/\*/g, '').trim();
      }
    }

    const placeholder = field.getAttribute('placeholder');
    if (placeholder && placeholder.trim() && placeholder.trim() !== ' ') {
      return placeholder.trim();
    }

    const name = field.getAttribute('name') || '';
    const match = name.match(/^contact\[(.*)\]$/);
    return match ? match[1].trim() : '';
  };

  const swishGetFieldValue = (field) => {
    if (!(field instanceof HTMLElement) || field.disabled) return '';

    if (field instanceof HTMLInputElement) {
      if (field.type === 'checkbox' || field.type === 'radio') {
        return field.checked ? (field.value || 'Yes') : '';
      }
      if (field.type === 'hidden' || field.type === 'file' || field.type === 'submit' || field.type === 'button') {
        return '';
      }
      return (field.value || '').trim();
    }

    if (field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement) {
      return (field.value || '').trim();
    }

    return '';
  };

  const swishEnsureGeneratedBodyField = (form) => {
    let bodyField = form.querySelector(`textarea[name="${SWISH_CONTACT_BODY_NAME}"], input[name="${SWISH_CONTACT_BODY_NAME}"]`);
    if (bodyField) return bodyField;

    bodyField = document.createElement('textarea');
    bodyField.name = SWISH_CONTACT_BODY_NAME;
    bodyField.hidden = true;
    bodyField.setAttribute('aria-hidden', 'true');
    bodyField.dataset.swishGeneratedContactBody = 'true';
    form.appendChild(bodyField);
    return bodyField;
  };

  const swishBuildContactBody = (form) => {
    const lines = [];
    const seen = new Set();

    SWISH_CONTACT_BODY_HIDDEN_FIELDS.forEach((fieldName) => {
      const hiddenField = form.querySelector(`[name="${swishEscapeSelectorValue(fieldName)}"]`);
      if (!(hiddenField instanceof HTMLInputElement || hiddenField instanceof HTMLTextAreaElement)) return;

      const value = (hiddenField.value || '').trim();
      if (!value) return;

      const label = swishGetContactFieldLabel(form, hiddenField) || fieldName.replace(/^contact\[(.*)\]$/, '$1');
      const line = `${label}: ${value}`;
      if (seen.has(line)) return;

      seen.add(line);
      lines.push(line);
    });

    Array.from(form.elements || []).forEach((field) => {
      if (!(field instanceof HTMLElement)) return;

      const name = field.getAttribute('name') || '';
      if (!name || !/^contact\[.*\]$/.test(name) || name === SWISH_CONTACT_BODY_NAME) return;

      const value = swishGetFieldValue(field);
      if (!value) return;

      const label = swishGetContactFieldLabel(form, field);
      const line = label ? `${label}: ${value}` : value;
      if (seen.has(line)) return;

      seen.add(line);
      lines.push(line);
    });

    return lines.join('\n').trim();
  };

  if (typeof window.SwishShopifyCaptcha.shouldAllowNativeSubmit !== 'function') {
    window.SwishShopifyCaptcha.shouldAllowNativeSubmit = function shouldAllowNativeSubmit(form) {
      return !!(
        form instanceof HTMLFormElement &&
        form.dataset &&
        form.dataset.swishCaptchaNativeSubmit === '1'
      );
    };
  }

  if (typeof window.SwishShopifyCaptcha.prepareForm !== 'function') {
    window.SwishShopifyCaptcha.prepareForm = function prepareForm(form) {
      if (!swishIsContactForm(form)) return;

      const originalAction = form.getAttribute('action') || form.dataset.swishCaptchaAction || '/contact';
      form.dataset.swishCaptchaAction = originalAction;
      form.setAttribute('action', originalAction);
      form.setAttribute('method', 'post');

      const bodyField = swishEnsureGeneratedBodyField(form);
      if (!(bodyField instanceof HTMLInputElement || bodyField instanceof HTMLTextAreaElement)) return;

      if (form.dataset.swishContactBodySerializationBound !== 'true') {
        form.dataset.swishContactBodySerializationBound = 'true';
        form.addEventListener('formdata', (event) => {
          // Keep generated summaries in the outgoing payload only. Never write
          // them into the customer's Notes field, even when it is left blank.
          const data = event.formData;
          if (!data.has(SWISH_CONTACT_BODY_NAME)) return;
          if (!String(data.get(SWISH_CONTACT_BODY_NAME) || '').trim()) {
            data.set(SWISH_CONTACT_BODY_NAME, swishBuildContactBody(form));
          }
        });
      }
    };
  }

  if (typeof window.SwishShopifyCaptcha.submitForm !== 'function') {
    window.SwishShopifyCaptcha.submitForm = function submitForm(form, options = {}) {
      if (!(form instanceof HTMLFormElement)) {
        return Promise.reject(new Error('Invalid form element.'));
      }

      window.SwishShopifyCaptcha.prepareForm(form);

      if (typeof options.onCaptchaReady === 'function') {
        options.onCaptchaReady({ protected: false });
      }

      const action = form.getAttribute('action') || form.dataset.swishCaptchaAction || '/contact';
      const endpoint = new URL(action, window.location.href).toString();
      const body = new URLSearchParams();
      const formData = new FormData(form);
      const allowedFieldNames = Array.isArray(options.fieldNames) && options.fieldNames.length
        ? new Set(options.fieldNames.map((name) => String(name)))
        : null;
      formData.forEach((value, name) => {
        if (typeof value === 'string' && (!allowedFieldNames || allowedFieldNames.has(name))) {
          body.append(name, value);
        }
      });

      if (options.fieldOverrides && typeof options.fieldOverrides === 'object') {
        Object.entries(options.fieldOverrides).forEach(([name, value]) => {
          if (value == null) return;
          body.set(String(name), String(value));
        });
      }

      const requestedTimeout = Number(options.timeoutMs);
      const timeoutMs = Number.isFinite(requestedTimeout) && requestedTimeout > 0
        ? requestedTimeout
        : 20000;
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      const timeout = controller
        ? window.setTimeout(() => controller.abort(), timeoutMs)
        : null;

      const submitNatively = () => {
        form.dataset.swishCaptchaNativeSubmit = '1';

        window.setTimeout(() => {
          try {
            if (typeof form.requestSubmit === 'function') {
              form.requestSubmit();
            } else {
              HTMLFormElement.prototype.submit.call(form);
            }
          } catch (error) {
            form.dataset.swishCaptchaNativeSubmit = '0';
            try { console.warn('[Swish Contact] Shopify native notification fallback failed.', error); } catch (_) {}
          }
        }, 0);

        return {
          protected: true,
          method: 'shopify-contact-native-fallback',
          notificationPosted: true,
          navigationExpected: true
        };
      };

      return fetch(endpoint, {
        method: 'POST',
        headers: {
          'accept': 'text/html',
          'content-type': 'application/x-www-form-urlencoded;charset=UTF-8'
        },
        body: body.toString(),
        credentials: 'same-origin',
        redirect: 'follow',
        signal: controller ? controller.signal : undefined
      }).then(async (response) => {
        const responseText = await response.text();
        const responseUrl = String(response.url || '');
        const challengeDetected = /\/challenge(?:[/?#]|$)/i.test(responseUrl) ||
          /<form[^>]+action=["'][^"']*\/challenge/i.test(responseText);

        if (!response.ok || challengeDetected) {
          return submitNatively();
        }

        return {
          protected: false,
          method: 'shopify-contact-fetch',
          notificationPosted: true,
          status: response.status
        };
      }).catch((error) => {
        if (error && error.name === 'AbortError') {
          return submitNatively();
        }
        return submitNatively();
      }).finally(() => {
        if (timeout !== null) {
          window.clearTimeout(timeout);
        }
      });
    };
  }

  if (typeof window.SwishShopifyCaptcha.bindContactForms !== 'function') {
    window.SwishShopifyCaptcha.bindContactForms = function bindContactForms(root = document) {
      swishCollectContactForms(root).forEach((form) => {
        window.SwishShopifyCaptcha.prepareForm(form);

        if (form.dataset.swishCaptchaBound !== 'true') {
          form.dataset.swishCaptchaBound = 'true';
          form.addEventListener('submit', () => {
            window.SwishShopifyCaptcha.prepareForm(form);
          }, true);
        }
      });
    };
  }

  if (typeof window.SwishShopifyCaptcha.bindEcomForms !== 'function') {
    window.SwishShopifyCaptcha.bindEcomForms = function bindEcomForms(root = document) {
      return window.SwishShopifyCaptcha.bindContactForms(root);
    };
  }

  const bindAllContactForms = () => {
    window.SwishShopifyCaptcha.bindContactForms(document);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAllContactForms, { once: true });
  } else {
    bindAllContactForms();
  }

  const observer = new MutationObserver((records) => {
    records.forEach((record) => {
      Array.from(record.addedNodes || []).forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (node.matches && node.matches('form')) {
          window.SwishShopifyCaptcha.bindContactForms(node.parentElement || document);
          return;
        }
        if (node.querySelector) {
          window.SwishShopifyCaptcha.bindContactForms(node);
        }
      });
    });
  });

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      observer.observe(document.body, { childList: true, subtree: true });
    }, { once: true });
  }
}
